import { useEffect, useState } from 'react';
import { tours } from '../constants';

import { useNavigate, useParams } from 'react-router-dom';
import { getPopular } from './PopularItems';

/**
 * Enhanced hook for searching and sorting tours with pagination
 */
export const useTourFilter = () => {
    const navigate = useNavigate();
    const { search } = useParams();

    // State variables
    const [matchingTours, setMatchingTours] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOption, setSortOption] = useState('');

    // Items per page
    const itemsPerPage = 6;

    // Synchronize search keyword with URL params
    useEffect(() => {
        setSearchKeyword(search || '');
        setCurrentPage(1); // Reset to first page on new search
    }, [search]);

    // Filter and sort tours whenever search keyword or sort option changes
    useEffect(() => {
        let filteredTours = searchKeyword 
            ? handleHeaderSearch(searchKeyword)
            : [...tours];

        // Apply sorting
        filteredTours = handleSortChange(filteredTours, sortOption);

        setMatchingTours(filteredTours);
    }, [searchKeyword, sortOption]);

    // Filters tours based on a search keyword
    const handleHeaderSearch = (searchTerm) => {
        const searchLower = searchTerm.toLowerCase();
        return tours.filter((tour) => (
            tour.title?.toLowerCase().includes(searchLower) ||
            tour.details.location?.toLowerCase().includes(searchLower) ||
            tour.details.tourType?.toLowerCase().includes(searchLower)
        ));
    };

    // Handles header search form submission
    const handleHeaderSearchSubmit = (searchQuery) => {
        const encodedQuery = encodeURIComponent(searchQuery);
        navigate(`/Tours/Search/${encodedQuery}`);
    };

    /**
     * Sorts tours based on the selected option
     * @param {Array} tours - The tours to sort
     * @param {string} sortOption - The sorting option
     * @returns {Array} - Sorted tours
     */
    const handleSortChange = (tours, sortOption) => {
        switch (sortOption) {
            case 'featured':
                return getPopular(tours);
            case 'lowPrice':
                return [...tours].sort((a, b) => a.details.price - b.details.price);
            case 'highPrice':
                return [...tours].sort((a, b) => b.details.price - a.details.price);
            default:
                return tours;
        }
    };

    /**
     * Handles sorting option change
     * @param {Object} selectedOption - The selected sorting option
     */
    const handleSort = (selectedOption) => {
        setSortOption(selectedOption ? selectedOption.value : '');
        setCurrentPage(1); // Reset to first page on sort change
    };

    // Pagination calculations
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedTours = matchingTours.slice(startIndex, endIndex);
    const totalPages = Math.ceil(matchingTours.length / itemsPerPage);

    // Function to handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0); // Optional: scroll to top on page change
    };

    return {
        searchKeyword,
        matchingTours,
        displayedTours,
        currentPage,
        totalPages,
        sortOption,
        handlePageChange,
        handleHeaderSearchSubmit,
        handleSort,
    };
};

// Function to filter tours by location
export const filterToursByLocation = (location) => {
    return tours.filter(tour => tour.details.location === location);
};